import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  styled,
  IconButton,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import * as Yup from "yup";
import { StyledButton } from "../../../shared/components/buttons/custom-button";
import FormBuilder from "../../../shared/components/form-builder/form-builder";
import { OptionType } from "../../../shared/components/form-builder/form-config.model";
import CustomTable from "../../../shared/components/table/custom-table";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../../shared/hooks/useApi";
import { Utills } from "../../../shared/app-configs/utilit";
import { getATCColumns, ATCDashboardRow } from "./cbLandingPageTable.config";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PageviewIcon from "@mui/icons-material/Pageview";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTransactionCertificateStore } from "../../../store/transaction-certificates.store";
import { atcService } from "../atc-service/atc.service";
import { MRT_ColumnDef } from "material-react-table";
import AlertPopup from "../../../shared/components/modals/alert-moadal";
import { buyerSellerDetailsService } from "../../v4/transaction-certificates/tc-service/buyer-seller-details.service";
import useNotification from "../../../shared/hooks/useNotification";
import ViewerModalPopup from "../../../shared/components/modals/viewerModal";
import PdfViewer from "../../../shared/components/preview/pdf-viewer";
import ATCAppConfig from "../../../shared/app-configs/atc-app-configs";
import { Download } from "@mui/icons-material";
import {
  downloadDocx,
  DownloadDocxOptions,
} from "../../../shared/components/document/docx-downloader";
// import { useRecentTEIds } from "../../../shared/hooks/useRecentTEIds";
import { isAdmin, isViewOnlyAdminMode } from "../../../shared/utils/admin-utils"

export const PageWrapper = styled("div")({
  padding: "0 25px",
  display: "grid",
  overflowY: "auto",
  flexDirection: "column",
  gap: "12px",
  flex: 1,
});

const ATCLandingPage: React.FC = () => {
  const formRef = useRef<any>(null);
  const { executeGet, loading, executeDelete } = useApi();
  const navigate = useNavigate();

  const [noOfRows, setNoOfRows] = useState<number>(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTcNumber, setPreviewTcNumber] = useState<string | null>(null);
  const { cbLicensingCode } = useTransactionCertificateStore();
  const [tableData, setTableData] = useState<ATCDashboardRow[]>([]);
  const [rawATCResponse, setRawATCResponse] = useState<any[]>([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [isResetEnabled, setIsResetEnabled] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [popUpTitle, setPopUpTitle] = useState("");
  const [popUpMessage, setPopUpMessage] = useState("");
  const [popUpBtnName, setPopUpBtnName] = useState("");
  const [selectedATC, setSelectedATC] = useState<{
    atcId: string;
    atcCbLicensingCode: string;
  } | null>(null);
  const { notify } = useNotification();
  const [selectedATCForView, setSelectedATCForView] = useState<{
    fileName: string;
    atcNo: string;
    atcVersionNo: string;
  } | null>(null);
  const [selectedATCIdForView, setSelectedATCIdForView] = useState<string>("");

  const togglePreviewDrawer = () => setPreviewOpen((prev) => !prev);

  const standardOptions: OptionType[] = [
    { label: "Organic Content Standard (OCS)", value: "OCS" },
    { label: "Responsible Down Standard (RDS)", value: "RDS" },
    { label: "Responsible Alpaca Standard (RAS)", value: "RAS" },
    { label: "Global Recycled Standard (GRS)", value: "GRS" },
    { label: "Responsible Wool Standard (RWS)", value: "RWS" },
    { label: "Content Claim Standard (CCS)", value: "CCS" },
    { label: "Recycled Claim Standard (RCS)", value: "RCS" },
    { label: "Responsible Mohair Standard (RMS)", value: "RMS" },
    { label: "GOTS (GOTS)", value: "GOTS" },
  ];
  const [isDownloading, setIsDownloading] = useState(false);

  interface ATCApiResponse {
    response: any[];
    no_of_records: number;
  }

  const fetchATCDashboard = async (
    filters: Record<string, string | number | string[]> = {}
  ) => {
    try {
      const resp = await executeGet(ATCAppConfig.GET_ATC_LICENSING_CODE_URL, {
        tcUploadCb: cbLicensingCode,
        atcCbLicensingCode: filters.cbLicensingCode as string,
        coname: (filters.buyerName as string) || "",
        teid: (filters.buyerTeId as string) || "",
        atcStandard:
          Array.isArray(filters.standard) && filters.standard.length
            ? JSON.stringify(filters.standard as string[])
            : "",
        uploadType: (filters.uploadType as string) || "",
        startDate: (filters.startDate as string) || "",
        endDate: (filters.endDate as string) || "",
        limit: pagination.pageSize,
        offset: pagination.pageIndex * pagination.pageSize,
      });

      const data = resp?.data as ATCApiResponse;
      setRawATCResponse(data?.response);
      setNoOfRows(data?.no_of_records);

      if (resp?.status === 200 && Array.isArray(data.response)) {
        const mapped: ATCDashboardRow[] = data.response.map((item: any) => ({
          tcNumber: item.atcNo || "",
          uploadType: item.uploadType || "",
          issueDate: item.atcDateOfIssue || "",
          buyerName: item.buyerName || "",
          sellerName: item.sellerName || "",
          cbLicensingCode: item.atcCbLicensingCode || "",
          tcStandard: Array.isArray(item.atcStandard)
            ? item.atcStandard
            : item.atcStandard
            ? [item.atcStandard]
            : [],
          certifiedWeight: item.atcCertifiedWeight || "",
          submissionDate: item.atcSubmissionDate || "",
          status: item.atcStatus || "",
          atcVersionNo: item.atcVersionNo || "",
          fileName: item.blobPath || "",
        }));

        setTableData(mapped);
      }
    } catch (e) {
      Utills.cl("ATC dashboard fetch failed", e);
    }
  };

  const fetchDeleteApi = async (atcId: string, atcCbLicensingCode: string) => {
    try {
      const response: any = await atcService.deleteATC(
        atcId,
        atcCbLicensingCode
      );
      if (response?.status === 200) {
        notify("ATU InValidated Sucessfully", "success");
        fetchATCDashboard();
      }
    } catch (err) {
      Utills.cl("Error deleting ATC", err);
    }
  };

  const fetchTEDetailsFromAPI = async (
    value: string,
    offset: number,
    type: string
  ): Promise<Array<{ TE_ID: string; account_name: string }>> => {
    try {
      const queryParams: Record<string, any> = {
        query_param: type === "query" ? value : "",
        offset,
        name: type === "name" ? value : "",
      };
      const response = await buyerSellerDetailsService.getTEDetails(
        queryParams
      );

      const data = response?.data || [];

      return data.map((item: any) => ({
        ...item,
        TE_ID: item.TE_ID,
        account_name: item.account_name,
      }));
    } catch (error) {
      console.error("Error fetching TE details:", error);
      notify("Failed to fetch TE details", "error");
      return [];
    }
  };

  const handleBuyerTEIdValueChange = (value: any) => {
    if (!value) {
      formRef.current?.setFieldValue("buyerTeId", "");
      formRef.current?.setFieldValue("buyerName", "");
      return;
    }
    //  Fix: strip any extra TE- prefix before saving
    const cleanTeid = (value.TE_ID || "").replace(/^TE-/, "");
    formRef.current?.setFieldValue("buyerTeId", cleanTeid);
    formRef.current?.setFieldValue("buyerName", value.account_name || "");
  };

  const handleBuyerNameValueChange = (value: any) => {
    if (!value) {
      formRef.current?.setFieldValue("buyerName", "");
      formRef.current?.setFieldValue("buyerTeId", "");
      return;
    }
    //  Same fix as TE-ID handler: strip "TE-" before saving
    const cleanTeid = (value.TE_ID || "").replace(/^TE-/, "");
    formRef.current?.setFieldValue("buyerName", value.account_name || "");
    formRef.current?.setFieldValue("buyerTeId", cleanTeid);
  };

  const sanitizeFilename = (name?: string): string => {
    if (!name) return "";
    return name.replace(/[^\w .-]/g, "_");
  };

  const formatAtcNo = (value: string): string => {
    const parts = value.split("-");
    if (parts.length >= 2) {
      return `${parts[parts.length - 2]}-${parts[parts.length - 1]}`;
    }
    return value;
  };

  const handleViewAtc = (row: ATCDashboardRow) => {
    const matchingAtc = rawATCResponse.find(
      (item) => item.atcNo === row.tcNumber
    );

    if (matchingAtc) {
      setSelectedATCIdForView(matchingAtc.atcId);
    }

    setPreviewTcNumber(row.tcNumber);
    setPreviewOpen(true);
  };

  const handleDownloadAtc = async (row: ATCDashboardRow) => {
    executeGet(ATCAppConfig.ATC_PREVIEW_DOWNLOAD, {
      filename: row?.fileName,
      atcNo: row?.tcNumber,
      atcVersionNo: row?.atcVersionNo,
    });
    setIsDownloading(true);
    const options: DownloadDocxOptions = {
      apiUrl: ATCAppConfig.ATC_PREVIEW_DOWNLOAD,
      params: {
        filename: row.fileName || "",
        atcNo: row.tcNumber,
        atcVersionNo: row.atcVersionNo || "",
      },
      headers: {
        // Optional headers if needed
      },
    };

    try {
      notify("Starting Download", "info");
      await downloadDocx(options);
      notify("File Downloaded", "success");
      setIsDownloading(false);
    } catch (error) {
      notify("Failed to download DOCX", "error");
    }
  };
  const columns: MRT_ColumnDef<any>[] = [
    {
      accessorKey: "actions",
      header: "",
      minSize: 100,
      maxSize: 120,
      Cell: ({ row }: { row: { original: ATCDashboardRow } }) => (
        <Box display="flex" gap={1}>
          <Tooltip title="View ATU">
            <IconButton
              size="small"
              onClick={() => handleViewAtc(row.original)}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {row.original.status === "Valid" && !isViewOnlyAdminMode() && (
            <Tooltip title="Invalidate">
              <IconButton
                size="small"
                onClick={() => {
                  setSelectedATC({
                    atcId: rawATCResponse.find(
                      (item) => item.atcNo === row.original.tcNumber
                    )?.atcId,
                    atcCbLicensingCode: cbLicensingCode,
                  });
                  setDialogOpen(true);
                  setPopUpTitle("Invalidate ATU");
                  setPopUpMessage(
                    "Are you sure you want to invalidate this ATU?"
                  );
                  setPopUpBtnName("Confirm");
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}{
          row?.original?.fileName &&
          <Tooltip title="Download">
            <IconButton
              size="small"
              onClick={() => handleDownloadAtc(row.original)}
            >
              <Download fontSize="small" />
            </IconButton>
          </Tooltip>
          }
        </Box>
      ),
    },
    {
      accessorKey: "tcNumber",
      header: "TC Number",
      minSize: 120,
      maxSize: 200,
    },
    { accessorKey: "uploadType", header: "Upload Type" },
    { accessorKey: "issueDate", header: "Issue Date" },
    { accessorKey: "buyerName", header: "Buyer Name" },
    { accessorKey: "sellerName", header: "Seller Name" },
    { accessorKey: "cbLicensingCode", header: "CB Licensing Code" },
    {
      accessorKey: "tcStandard",
      header: "TC Standard",
      Cell: ({ cell }) => {
        const value = cell.getValue() as string[];
        return Array.isArray(value) ? value.join(", ") : value || "";
      },
    },
    { accessorKey: "certifiedWeight", header: "TC Certified Weight" },
    { accessorKey: "submissionDate", header: "Submission Date" },
    { accessorKey: "status", header: "Status" },
  ];

  const formConfig = {
    pageName: "atc-landing-page",
    initialValues: {
      buyerName: null,
      buyerTeId: null,
      cbLicensingCode: "",
      uploadType: "",
      standard: [],
    },
    validationSchema: Yup.object().shape({
      buyerName: Yup.mixed().nullable(),
      buyerTeId: Yup.mixed().nullable(),
      cbLicensingCode: Yup.string().nullable(),
      uploadType: Yup.string().nullable(),
      standard: Yup.array().nullable(),
    }),
    enableReinitialize: true,
    sections: [
      {
        fields: [
          {
            label: "Buyer CO Name",
            name: "buyerName",
            type: "search",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            props: {
              placeholder: "Search Buyer Name",
              variant: "outlined",
              displayKey: "account_name",
              onChange: handleBuyerNameValueChange,
              onSearch: (query: string, offset: number) =>
                fetchTEDetailsFromAPI(query, offset, "name"),
            },
          },
          {
            label: "Buyer CO TE-ID",
            name: "buyerTeId",
            type: "search",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            props: {
              size: "small",
              placeholder: "Search using TE-ID",
              displayKey: "TE_ID",
              variant: "outlined",
              restrictInputFormat: true,
              fullWidth: true,
              onChange: handleBuyerTEIdValueChange,
              onSearch: (query: string, offset: number) =>
                fetchTEDetailsFromAPI(query, offset, "query"),
            },
          },
          {
            label: "Upload Type",
            name: "uploadType",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            props: {
              options: [
                { value: "ATU", label: "Alternative Transaction Upload" },
                { value: "GOTS", label: "GOTS" },
              ],
              placeholder: "Select",
            },
          },
          {
            label: "CB Licensing Code",
            name: "cbLicensingCode",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            props: {
              size: "small",
              placeholder: "Enter CB Licensing Code",
              defaultValue: cbLicensingCode ?? "",
            },
          },
          {
            label: "Standard",
            name: "standard",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            props: {
              multiple: true,
              enableSearch: true,
              options: standardOptions,
              placeholder: "Select",
            },
          },
        ],
      },
    ],
    handleSubmit: async (values: any) => handleSearch(values),
  };

  const handleSearch = async (values: Record<string, any>) => {
    await fetchATCDashboard(values);
  };

  const handleReset = () => {
    formRef.current.resetForm();
    fetchATCDashboard({});
  };

  const handleConfirmDelete = async () => {
    if (selectedATC) {
      await fetchDeleteApi(selectedATC.atcId, cbLicensingCode);
    }
    setDialogOpen(false);
  };

  const handleCancelDelete = () => {
    setDialogOpen(false);
    setSelectedATC(null);
  };

  const handleAddNewATC = () => navigate("/create-atc");

  useEffect(() => {
    if (!formRef.current) return;
    const { values } = formRef.current;

    const hasValue =
      (values.buyerName !== null && values.buyerName !== "") ||
      (values.buyerTeId !== null && values.buyerTeId !== "") ||
      (values.cbLicensingCode !== null && values.cbLicensingCode !== "") ||
      (values.uploadType !== null && values.uploadType !== "") ||
      (Array.isArray(values.standard) && values.standard.length > 0);

    setIsResetEnabled(hasValue);
  }, [formRef.current?.values]);

  useEffect(() => {
    fetchATCDashboard();
  }, []);

  useEffect(() => {
    if(!loading){
      fetchATCDashboard(formRef.current?.values || {});
    }
  }, [pagination]);

  return (
    <PageWrapper>
      <Typography variant="h6" gutterBottom align="left" fontWeight={"600"}>
        Alternative Transaction Upload
      </Typography>

      {/* Admin users in view-only mode should not see Alternative Transaction Upload button */}
      {!isViewOnlyAdminMode() && (
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          width="100%"
          mb="12px"
          gap={2}
        >
          <StyledButton
            onClick={handleAddNewATC}
            variant="contained"
            color="warning"
            sx={{ height: "40px", mb: 2 }}
          >
            Alternative Transaction Upload
          </StyledButton>
        </Box>
      )}

      <FormBuilder formConfig={formConfig} formRef={formRef} />

      <Grid
        item
        md={12}
        lg={12}
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        gap={2}
      >
        <StyledButton onClick={() => formRef.current.submitForm()}>
          Search
        </StyledButton>
        <StyledButton onClick={handleReset} disabled={!isResetEnabled}>
          Reset
        </StyledButton>
      </Grid>

      <CustomTable
        columns={columns}
        data={tableData}
        title="ATU Data"
        rowCount={noOfRows}
        enableTopToolbar={true}
        enableBottomToolbar={true}
        isLoadingTodos={loading}
        paginations={pagination}
        setPaginations={setPagination}
      />

      <ViewerModalPopup
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setSelectedATCForView(null);
          setSelectedATCIdForView("");
        }}
        title={`Preview - ${previewTcNumber || ""}`}
      >
        <div style={{ width: "100%", height: "100vh" }}>
          {selectedATCForView ? (
            <PdfViewer
              apiUrl={ATCAppConfig.ATC_PREVIEW}
              params={{
                filename: selectedATCForView.fileName,
                atcNo: selectedATCForView.atcNo,
                atcVersionNo: selectedATCForView.atcVersionNo,
              }}
              width="100%"
              height="100%"
            />
          ) : selectedATCIdForView ? (
            <PdfViewer
              apiUrl={ATCAppConfig.ATC_DATA_PREVIEW}
              params={{
                atcId: selectedATCIdForView,
              }}
              width="100%"
              height="100%"
            />
          ) : (
            <Typography>Loading PDF...</Typography>
          )}
        </div>
      </ViewerModalPopup>

      <AlertPopup
        open={dialogOpen}
        title={popUpTitle}
        message={popUpMessage}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        confirmText={popUpBtnName}
        cancelText="Cancel"
      />
    </PageWrapper>
  );
};

export default ATCLandingPage;
